/**
 * Local-storage backed hooks for The Weave loom — member-side state.
 *
 * - useSavedThreads: a member's saved/pinned threads (their "loomboard")
 * - useDailyPullState: today's pull count + session id, persisting across
 *   refreshes within a single day so the daily ritual framing feels real.
 *
 * Both are browser-only and survive page reload. When Memberstack auth is
 * wired later, the same shapes can be synced to a backend collection keyed
 * on member_id without changing the components.
 */
import { useCallback, useEffect, useState } from "react";

const SAVED_KEY = "weave:saved-threads:v1";
const DAILY_KEY = "weave:daily-pulls:v1";
const GUEST_KEY = "loom_pull_count";
export const GUEST_FREE_LIMIT = 3;

// Same-tab pub/sub — Storage events only fire across tabs, not within one.
const subscribers = new Set();
function broadcast(key) {
  subscribers.forEach((fn) => {
    try {
      fn(key);
    } catch {
      /* swallow — a misbehaving subscriber shouldn't break others */
    }
  });
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function safeRead(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeWrite(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    broadcast(key);
  } catch {
    /* quota or disabled — fine, in-memory only */
  }
}

// --------------------------- Saved threads ---------------------------

export function useSavedThreads() {
  const [saved, setSaved] = useState(() => safeRead(SAVED_KEY, []));

  // Cross-tab AND same-tab sync
  useEffect(() => {
    function onStorage(e) {
      if (e.key === SAVED_KEY) {
        setSaved(safeRead(SAVED_KEY, []));
      }
    }
    function onLocal(key) {
      if (key === SAVED_KEY) {
        setSaved(safeRead(SAVED_KEY, []));
      }
    }
    window.addEventListener("storage", onStorage);
    subscribers.add(onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      subscribers.delete(onLocal);
    };
  }, []);

  const persist = useCallback((next) => {
    setSaved(next);
    safeWrite(SAVED_KEY, next);
  }, []);

  const save = useCallback(
    (card) => {
      if (!card?.id) return;
      if (saved.some((c) => c.id === card.id)) return;
      const next = [
        { ...card, savedAt: new Date().toISOString() },
        ...saved,
      ].slice(0, 50);
      persist(next);
    },
    [saved, persist]
  );

  const remove = useCallback(
    (id) => persist(saved.filter((c) => c.id !== id)),
    [saved, persist]
  );

  const isSaved = useCallback((id) => saved.some((c) => c.id === id), [saved]);

  const toggle = useCallback(
    (card) => {
      if (!card?.id) return;
      if (saved.some((c) => c.id === card.id)) {
        persist(saved.filter((c) => c.id !== card.id));
      } else {
        const next = [
          { ...card, savedAt: new Date().toISOString() },
          ...saved,
        ].slice(0, 50);
        persist(next);
      }
    },
    [saved, persist]
  );

  return { saved, save, remove, isSaved, toggle };
}

// --------------------------- Daily pull state (members) ---------------------------

export function useDailyPullState() {
  const [state, setState] = useState(() => {
    const stored = safeRead(DAILY_KEY, null);
    if (stored && stored.date === todayKey()) return stored;
    return { date: todayKey(), count: 0, sessionId: null };
  });

  const setPull = useCallback((nextCount, sessionId) => {
    const next = {
      date: todayKey(),
      count: nextCount,
      sessionId: sessionId ?? null,
    };
    setState(next);
    safeWrite(DAILY_KEY, next);
  }, []);

  const reset = useCallback(() => {
    const fresh = { date: todayKey(), count: 0, sessionId: null };
    setState(fresh);
    safeWrite(DAILY_KEY, fresh);
  }, []);

  return { state, setPull, reset };
}

// --------------------------- Guest pull count (lifetime, per device) ---------------------------

/**
 * Tracks the total pulls a non-member has taken on this device.
 * Stored under the simple `loom_pull_count` key as agreed with the
 * awake.wearetheweave.co.uk agent. Lifetime counter — does NOT reset daily.
 */
export function useGuestPullCount() {
  const [count, setCount] = useState(() => {
    const raw = safeRead(GUEST_KEY, 0);
    return typeof raw === "number" && raw >= 0 ? raw : 0;
  });

  useEffect(() => {
    function onStorage(e) {
      if (e.key === GUEST_KEY) {
        const raw = safeRead(GUEST_KEY, 0);
        setCount(typeof raw === "number" && raw >= 0 ? raw : 0);
      }
    }
    function onLocal(key) {
      if (key === GUEST_KEY) {
        const raw = safeRead(GUEST_KEY, 0);
        setCount(typeof raw === "number" && raw >= 0 ? raw : 0);
      }
    }
    window.addEventListener("storage", onStorage);
    subscribers.add(onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      subscribers.delete(onLocal);
    };
  }, []);

  const increment = useCallback(() => {
    setCount((prev) => {
      const next = prev + 1;
      safeWrite(GUEST_KEY, next);
      return next;
    });
  }, []);

  return { count, increment, limit: GUEST_FREE_LIMIT };
}
